import { DATE_RANGE_STORAGE_KEY, type DateRangeDays } from "./gantt-date-range-constants";
import { DateHelper } from "@bryntum/core-thin";
import type { SchedulerInitialDateRange } from "./resource-schedule/resource-schedule-types";

export const getSavedDateRange = (): DateRangeDays => {
   const saved = localStorage.getItem(DATE_RANGE_STORAGE_KEY);
   if (saved === null) return null;
   const parsed = Number(saved);
   return isNaN(parsed) ? null : (parsed as DateRangeDays);
};

export const computeInitialDates = (
   selectedDateRange: DateRangeDays,
   defaultStart: Date,
   defaultEnd: Date,
): { startDate: Date; endDate: Date } => {
   if (selectedDateRange !== null) {
      const startDate = new Date();
      const endDate = DateHelper.add(startDate, selectedDateRange, "day");
      return { startDate, endDate };
   }
   return { startDate: defaultStart, endDate: defaultEnd };
};

export const computeRangeBounds = (
   days: number,
   anchor?: Date,
): {
   rangeStart: Date;
   rangeEnd: Date;
} => {
   const rangeStart = anchor ?? new Date();
   const rangeEnd = DateHelper.add(rangeStart, days, "day");
   return { rangeStart, rangeEnd };
};

export const computeRangeBoundsWithClearedTime = (
   days: number,
   anchor?: Date,
): {
   rangeStart: Date;
   rangeEnd: Date;
} => {
   const { rangeStart, rangeEnd } = computeRangeBounds(days, anchor);
   return {
      rangeStart: DateHelper.clearTime(rangeStart, true),
      rangeEnd: DateHelper.clearTime(rangeEnd, true),
   };
};

export const computeDefaultRange = (
   mainUnitOffset: number,
   multipliers: { back: number; forward: number },
): { resetStart: Date; resetEnd: Date } => {
   const resetStart = new Date();
   const resetEnd = new Date();
   resetStart.setTime(resetStart.getTime() - mainUnitOffset * multipliers.back);
   resetEnd.setTime(resetEnd.getTime() + mainUnitOffset * multipliers.forward);
   return { resetStart, resetEnd };
};

export const getMainUnitInMs = (mainUnit: string): number => {
   const ONE_DAY = 1000 * 60 * 60 * 24;
   switch (mainUnit) {
      case "day":
         return ONE_DAY;
      case "week":
         return ONE_DAY * 7;
      case "month":
         return ONE_DAY * 30;
      case "year":
         return ONE_DAY * 365;
      default:
         return ONE_DAY * 365;
   }
};

const PRESET_THRESHOLDS: ReadonlyArray<{ maxDays: number; presetId: string }> = [
   { maxDays: 28, presetId: "my_weekAndDay-54by80" }, // 1–4 weeks
   { maxDays: 56, presetId: "my_weekDateAndMonth" }, // ~2 months
   { maxDays: 91, presetId: "my_weekAndMonth" }, // ~quarter
   { maxDays: 182, presetId: "my_monthAndYear" }, // ~half year
   { maxDays: 730, presetId: "my_year-200by100" }, // 1–2 years
   { maxDays: 1825, presetId: "my_year" }, // 3–5 years
   { maxDays: Infinity, presetId: "my_year-50by100" }, // 5+ years
];

export const getPresetForDays = (days: number): string => {
   return PRESET_THRESHOLDS.find((t) => days <= t.maxDays)!.presetId;
};

export const getSchedulerInitialDateRange = (days: number): SchedulerInitialDateRange => {
   const { rangeStart, rangeEnd: maxDate } = computeRangeBoundsWithClearedTime(days);
   return {
      minDate: rangeStart,
      maxDate,
      viewPreset: getPresetForDays(days),
      visibleDate: rangeStart,
   };
};
